import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import RSButton from "ds_legacy/components/RSButton";
import { GREY_600, TEXT_DARK_SECONDARY } from "ds_legacy/materials/colors";
import { dp, margin } from "ds_legacy/materials/metrics";
import { FONT_SIZE_16, Subheading } from "ds_legacy/materials/typography";
import { useTranslations } from "translations";
import { ConfirmationDialogProps } from "./useConfirmationDialog";

function ConfirmationDialog({
  cancelButtonProps,
  cancelText,
  componentContent,
  confirmButtonProps,
  content,
  headerIcon,
  id,
  nodeContent,
  onCancel,
  onSubmit,
  opened,
  queryProgress,
  submitText,
  sx,
  title,
  useNewDesign,
}: ConfirmationDialogProps<boolean>) {
  const translations = useTranslations();

  return useNewDesign ? (
    <Dialog
      data-testid={id}
      id={id}
      open={opened}
      PaperProps={{
        "aria-modal": true,
        sx: {
          alignItems: "center",
          borderRadius: dp(8),
          maxWidth: dp(400),
          padding: dp(24),
          ...sx,
        },
      }}
    >
      {headerIcon && (
        <div className="flex w-full justify-center pt-2">{headerIcon}</div>
      )}
      <DialogTitle
        sx={{
          fontSize: FONT_SIZE_16,
          fontWeight: 700,
          lineHeight: dp(20),
          mx: 0,
          px: 0,
          textAlign: "center",
        }}
      >
        {title ?? ""}
      </DialogTitle>
      {content && (
        <DialogContent sx={{ mx: 0, px: 0 }}>
          <DialogContentText
            sx={{
              color: GREY_600,
              fontSize: dp(14),
              lineHeight: dp(18),
              mx: 0,
              px: 0,
              textAlign: "center",
            }}
          >
            {content}
          </DialogContentText>
        </DialogContent>
      )}
      {componentContent && (
        <DialogContent sx={{ mx: 0, px: 0 }}>{componentContent}</DialogContent>
      )}
      {nodeContent && (
        <DialogContent sx={{ mx: 0, px: 0 }}>
          <Subheading style={{ color: TEXT_DARK_SECONDARY }}>
            {nodeContent}
          </Subheading>
        </DialogContent>
      )}
      <DialogActions
        sx={{
          flexDirection: "column",
          gap: dp(8),
          justifyContent: "center",
          width: "100%",
        }}
      >
        <RSButton
          buttonWrapperStyle={{ margin: margin(0), width: "100%" }}
          color="primary"
          id={`confirm-${id}`}
          loading={queryProgress || "na"}
          style={{ margin: margin(0), width: "100%" }}
          variant="contained"
          onClick={() => onSubmit(true)}
          {...confirmButtonProps}
        >
          {submitText || translations.actions.confirm}
        </RSButton>
        <RSButton
          buttonWrapperStyle={{ margin: margin(0), width: "100%" }}
          color="primary"
          id={`cancel-${id}`}
          loading="na"
          style={{ margin: margin(0), width: "100%" }}
          variant="outlined"
          onClick={onCancel}
          {...cancelButtonProps}
        >
          {cancelText || translations.actions.cancel}
        </RSButton>
      </DialogActions>
    </Dialog>
  ) : (
    <Dialog
      data-testid={id}
      fullWidth
      id={id}
      open={opened}
      PaperProps={{
        "aria-modal": "true",
      }}
    >
      <DialogTitle>{title ?? ""}</DialogTitle>
      {content && (
        <DialogContent>
          <DialogContentText>{content}</DialogContentText>
        </DialogContent>
      )}
      {componentContent && <DialogContent>{componentContent}</DialogContent>}
      {nodeContent && (
        <DialogContent>
          <Subheading style={{ color: TEXT_DARK_SECONDARY }}>
            {nodeContent}
          </Subheading>
        </DialogContent>
      )}
      <DialogActions>
        <RSButton
          color="primary"
          id="cancel"
          loading="na"
          variant="text"
          onClick={onCancel}
          {...cancelButtonProps}
        >
          {cancelText || translations.actions.cancel}
        </RSButton>
        <RSButton
          color="primary"
          id="confirm"
          loading={queryProgress || "na"}
          variant="text"
          onClick={() => onSubmit(true)}
          {...confirmButtonProps}
        >
          {submitText || translations.actions.confirm}
        </RSButton>
      </DialogActions>
    </Dialog>
  );
}

export default ConfirmationDialog;
